import React, { useState, useEffect, useRef, useCallback } from "react";
import Image from "../fragments/Image";
import { Link } from "react-router-dom";
import SmartLink from "../fragments/SmartLink";

const PageProject = ({ project, galleryView, fadeOut }) => {

    const [infoOpen, setInfoOpen] = useState(false);

    const projectHeaderRef = useRef(null);
    const projectRef = useRef(null);

    const handleResize = useCallback(() => {
        if (projectHeaderRef.current) {
            const height = projectHeaderRef.current.clientHeight;
            document.documentElement.style.setProperty('--project-header-height', `${height}px`);
        }
    }, []);

    const handleScroll = useCallback(() => {

    }, []);

    function getOffsetTop(element) {
        let offsetTop = 0;
        while (element) {
            offsetTop += element.offsetTop;
            element = element.offsetParent;
        }
        return offsetTop;
    }

    const toggleInfo = useCallback(() => {
        setInfoOpen(prevState => {
            const nextState = !prevState;

            // Scroll project into view when opening
            if (!prevState && projectRef.current) {
                const rect = projectRef.current.getBoundingClientRect();
                const scrollTop = window.scrollY || document.documentElement.scrollTop;
                const topPos = getOffsetTop(projectRef.current) - document.getElementsByClassName('c-header')[0].clientHeight + 2;
                console.log('scrolling to', topPos);
                window.scrollTo({
                    top: topPos,
                    behavior: 'smooth'
                });
            }

            // Lock or unlock scroll
            if (!prevState) {
                // Opening: lock scroll
                document.documentElement.style.overflow = 'hidden';
                document.body.style.overflow = 'hidden';
            } else {
                // Closing: unlock scroll
                document.documentElement.style.overflow = '';
                document.body.style.overflow = '';
            }

            // Recalculate layout
            setTimeout(() => {
                handleResize();
                handleScroll();
            }, 0);

            return nextState;
        });
    }, [handleResize, handleScroll]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            handleResize();
        }, 0);

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll);
        
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleResize, handleScroll]);

    useEffect(() => {
        if (project) {
            const timeoutId = setTimeout(() => {
                handleResize();
                handleScroll(); 
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [project, infoOpen, handleResize, handleScroll]);

    if (!project) return null;

    return (
        <div className={`c-projects__project ${infoOpen ? 'state-info-open' : ''} ${fadeOut ? 'state-fade-out' : ''}`} ref={projectRef}>
            <div className="c-projects__project__header">
                <div className="c-projects__project__header__inner" ref={projectHeaderRef}>
                    <span className="c-projects__project__header__counter" dangerouslySetInnerHTML={{ __html: '(1/' + project.slides.length + ')' }}></span>
                    <h2 className="c-projects__project__header__title" dangerouslySetInnerHTML={{ __html: project.title }}></h2>
                    <h3 className="c-projects__project__header__credit" dangerouslySetInnerHTML={{ __html: project.credit }}></h3>
                    <span 
                        className="c-projects__project__header__information" 
                        dangerouslySetInnerHTML={{ __html: "Project Information" }} 
                        onClick={toggleInfo}
                    ></span>
                </div>
                <div className="c-projects__project__header__content">
                    <div className="c-projects__project__header__content__inner">
                        <div className="scope-text scope-text--faded" dangerouslySetInnerHTML={{ __html: project.information }} />
                        {project.media && project.media.length > 0 && (
                            <>
                                <h3 className="c-projects__project__header__content__heading">Media</h3>
                                <ul className="c-projects__project__header__media">
                                    {project.media.map((mediaItem, index) => (
                                        <React.Fragment key={index}>
                                            {mediaItem.url ? (
                                                <li className="c-projects__project__header__media__item">
                                                    <a 
                                                        className="c-projects__project__header__media__item__inner c-projects__project__header__media__item__inner--link" 
                                                        href={mediaItem.url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        dangerouslySetInnerHTML={{ __html: '<span>' + mediaItem.title + '</span><span>' + mediaItem.year + '</span>' }}
                                                    ></a>
                                                </li>
                                            ) : (
                                                <li className="c-projects__project__header__media__item">
                                                    <span 
                                                        className="c-projects__project__header__media__item__inner" 
                                                        dangerouslySetInnerHTML={{ __html: '<span>' + mediaItem.title + '</span><span>' + mediaItem.year + '</span>' }}
                                                    ></span>
                                                </li>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {galleryView &&
                <>
                    {project.slides.map((slide, index) => {
                        if (slide.type === 'inset' && index === 0) {
                            return (
                                <SmartLink
                                    to={`${project.url}`}
                                    className={`c-projects__project__slide c-projects__project__slide--index-${index} c-projects__project__slide--inset ${slide.width < slide.height ? 'c-projects__project__slide--portrait' : ''}`} 
                                    key={index}
                                >
                                    <div className="c-projects__project__slide__inner">
                                        <Image src={slide.src} width={slide.width} height={slide.height} alt={slide.alt} />
                                    </div>
                                </SmartLink>
                            );
                        } else if (slide.type === 'wide' && index === 0) {
                            return (
                                <SmartLink
                                    to={`${project.url}`}
                                    className={`c-projects__project__slide c-projects__project__slide--index-${index} c-projects__project__slide--wide ${slide.width < slide.height ? 'c-projects__project__slide--portrait' : ''}`} 
                                    key={index}
                                >
                                    <div className="c-projects__project__slide__inner">
                                        <Image src={slide.src} width={slide.width} height={slide.height} alt={slide.alt} />
                                    </div>
                                </SmartLink>
                            );
                        } else if (slide.type === 'two_columns' && index === 0) {
                            return (
                                <SmartLink
                                    to={`${project.url}`}
                                    className={`c-projects__project__slide c-projects__project__slide--index-${index} c-projects__project__slide--two-columns`}
                                    key={index}
                                >
                                    <div className="c-projects__project__slide__inner">
                                        <div className={`c-projects__project__slide__column c-projects__project__slide__column--left`}>
                                            <Image src={slide.left_src} width={slide.left_width} height={slide.left_height} alt={slide.left_alt} />
                                        </div>
                                        <div className={`c-projects__project__slide__column c-projects__project__slide__column--right`}>
                                            <Image src={slide.right_src} width={slide.right_width} height={slide.right_height} alt={slide.right_alt} />
                                        </div>
                                    </div>
                                </SmartLink>
                            );
                        }
                        
                        // Return null for unknown slide types
                        return null;
                    })}
                </>
            }
            {!galleryView &&
                <div className="c-projects__project__list">
                    {project.slides.map((slide, index) => {
                        if (slide.type === 'inset' || slide.type === 'wide') {
                            return (
                                <SmartLink
                                    to={`${project.url}#slide-${(index + 1)}`}
                                    className={`c-projects__project__list__thumb ${slide.height / slide.width > 1.5 ? 'state-fill' : ''}`}
                                    key={index}
                                >
                                    <Image src={slide.src} alt={slide.alt} />
                                </SmartLink>
                            );
                        } else if (slide.type === 'two_columns') {
                            return (
                                <React.Fragment key={index}>
                                    <SmartLink
                                        to={`${project.url}#slide-${(index + 1)}`}
                                        className={`c-projects__project__list__thumb ${slide.left_height / slide.left_width > 1.5 ? 'state-fill' : ''}`}
                                    >
                                        <Image src={slide.left_src} alt={slide.left_alt} />
                                    </SmartLink>
                                    <SmartLink
                                        to={`${project.url}#slide-${(index + 1)}`}
                                        className={`c-projects__project__list__thumb ${slide.right_height / slide.right_width > 1.5 ? 'state-fill' : ''}`}
                                    >
                                        <Image src={slide.right_src} alt={slide.right_alt} />
                                    </SmartLink>
                                </React.Fragment>
                            );
                        }
                        
                        // Return null for unknown slide types
                        return null;
                    })}
                </div>
            }
        </div>
    );
};

export default PageProject;